import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { InboundItemRow, InboundRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 后端写入值：WAIT_CHECK（采购收货生成）/ DRAFT（手工建单）/ DONE / CANCELED。 */
type StatusFilter = 'ALL' | 'WAIT_CHECK' | 'DRAFT' | 'DONE' | 'CANCELED';

const STATUS_META: Record<string, { text: string; color: string }> = {
  WAIT_CHECK: { text: '待入库确认', color: 'processing' },
  DRAFT: { text: '待入库', color: 'gold' },
  DONE: { text: '已入库', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' }
};

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待入库确认', value: 'WAIT_CHECK' },
  { label: '待入库', value: 'DRAFT' },
  { label: '已入库', value: 'DONE' }
];

/** 入库单：列表 + 详情 + 确认入库（按合格数量入库并写流水）。 */
export default function InboundList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['inbound', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.inbound.list({ lastId: pageParam, size: PAGE_SIZE, inboundStatus: status === 'ALL' ? undefined : status }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['inbound', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.inbound.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const confirm = useMutation({
    mutationFn: () => hq.inbound.confirm(currentId ?? ''),
    onSuccess: async (result) => {
      message.success(`入库完成：${result.bizNo}，共 ${result.totalQty}`);
      await queryClient.invalidateQueries({ queryKey: ['inbound'] });
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '确认入库失败')
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<InboundRow> = [
    {
      title: '入库单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '收货仓', dataIndex: 'warehouseName', width: 140, render: (value: string | null) => value || '-' },
    { title: '入库类型', dataIndex: 'inType', width: 130, render: (value: string) => value || '-' },
    { title: '供应商', dataIndex: 'supplierName', width: 160, render: (value: string | null) => value || '-' },
    { title: '来源单号', dataIndex: 'sourceBizNo', width: 200, render: (value: string | null) => value || '-' },
    {
      title: '入即出配对',
      dataIndex: 'pairBizNo',
      width: 240,
      render: (value: string | null, row) =>
        row.inType === 'OUTER_SUPPLY' || row.outerSourceFlag === 1 ? (
          <Space size={4}>
            <Tag color="purple">外仓代履</Tag>
            <Typography.Text copyable={Boolean(value)}>{value || '-'}</Typography.Text>
          </Space>
        ) : (
          '-'
        )
    },
    {
      title: '状态',
      dataIndex: 'inboundStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    { title: '验收时间', dataIndex: 'checkAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<InboundItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    { title: '效期', dataIndex: 'expiryDate', width: 120, render: (value: string | null) => formatDisplayDate(value) },
    { title: '计划', dataIndex: 'planQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '验收', dataIndex: 'checkQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '合格', dataIndex: 'qualifiedQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '不合格',
      dataIndex: 'unqualifiedQty',
      width: 90,
      align: 'right',
      render: (value: number) => (value > 0 ? <Typography.Text type="danger">{formatQty(value)}</Typography.Text> : '-')
    },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const canConfirm = detail.data ? !['DONE', 'CANCELED'].includes(detail.data.inbound.inboundStatus) : false;

  return (
    <Card
      className="page-card"
      title="入库单"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter> value={status} options={FILTERS} onChange={(value) => setStatus(value)} style={{ marginBottom: 12 }} />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="入库单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<InboundRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无入库单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.inbound.bizNo ?? '入库单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
        extra={
          canConfirm ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={confirm.isPending}
              onClick={() =>
                modal.confirm({
                  title: '确认入库',
                  content: '确认后将按合格数量增加库存并写入库存流水，不可撤销。',
                  okText: '确认入库',
                  onOk: () => confirm.mutateAsync()
                })
              }
            >
              确认入库
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.inbound.inboundStatus]?.text ?? detail.data.inbound.inboundStatus}
              </Descriptions.Item>
              <Descriptions.Item label="收货仓">{detail.data.inbound.warehouseName || '-'}</Descriptions.Item>
              <Descriptions.Item label="供应商">{detail.data.inbound.supplierName || '-'}</Descriptions.Item>
              <Descriptions.Item label="来源单号">{detail.data.inbound.sourceBizNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="入即出配对单号">
                {detail.data.inbound.pairBizNo || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.inbound.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.inbound.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="业务时间">{formatDisplayTime(detail.data.inbound.bizDate)}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.data.inbound.remark || '-'}</Descriptions.Item>
            </Descriptions>
            <Table<InboundItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
